// src/pages/Login.jsx - Refactored to use new reusable components
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyPathLanguage } from '../i18n';
import { useUser } from '../context/UserContext';
import { ROUTES, localePath, parseLocalePath } from '../constants/Routes';
import AuthLayout from '../components/auth/AuthLayout';
import { AUTH_LINK_CLASS } from '../components/auth/authLinkStyles';
import AuthHeader from '../components/auth/AuthHeader';
import TextField from '../components/auth/TextField';
import PasswordField from '../components/auth/PasswordField';
import PrimaryButton from '../components/auth/PrimaryButton';
import SocialButtons from '../components/auth/SocialButtons';
import { logUserActivity, recordLoginConversion, setLanguage as apiSetLanguage } from '../utils/api';
import { supportedLanguageCodes } from '../i18n';
import { useConversionRef } from '../hooks/useConversionRef';

// Simple PageTitle component
const PageTitle = ({ title }) => {
  React.useEffect(() => {
    document.title = title || 'Bloom IQ';
  }, [title]);

  return null;
};

const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();
  // Routes are declared with literal `/en/login` / `/es/login` paths (no
  // `:lang` dynamic segment), so `useParams().lang` is always undefined.
  // Derive the locale from the pathname instead.
  const { locale: lang } = parseLocalePath(location.pathname);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useUser();
  const conversionRef = useConversionRef();

  // Honour the URL locale (e.g. /es/login) so the page renders in the
  // visitor's marketing-site language. Firestore reconciliation happens
  // inside UserContext after sign-in; we do not write a language for
  // unauthenticated users.
  useEffect(() => {
    applyPathLanguage(location.pathname);
  }, [location.pathname]);

  // Email whitelist validation (only enforced in development)
  const isEmailAllowed = (email) => {
    if (import.meta.env.PROD) return true;

    const allowedEmails = ['alemoraesc@me.com', 'dev.test20220504@gmail.com', 'pabloespinozaculiacan@gmail.com', 'bloomtestdev@gmail.com', 'cmoore@northbayproduce.com', 'sean_mccauley@hotmail.com', 'iris.carrasco@amhpac.org', 'flara@amhpac.org', 'gustavo.robles@amhpac.org', 'ejec.cumplimiento@amhpac.org', 'inteligencia@amhpac.org'];
    const allowedDomains = ['@admin.com', '@bloomiqtechnologies.com', '@bloomiq.io'];

    if (allowedEmails.includes(email.toLowerCase())) return true;
    return allowedDomains.some(domain => email.toLowerCase().endsWith(domain));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check email whitelist before attempting login
    if (!isEmailAllowed(email)) {
      setError(t('errors.accessRestricted'));
      setLoading(false);
      return;
    }

    try {
      await signIn(email, password);
      // Persist the URL locale we logged in from (/es/login, /en/login)
      // to Firestore before navigating. This is an idempotent merge write
      // and guarantees the authenticated app renders in the same language
      // as the login page — without it, legacy users whose Firestore doc
      // still holds `en` would get flipped back to English by the
      // getLanguage fetch in UserContext.
      if (supportedLanguageCodes.includes(lang)) {
        try {
          await apiSetLanguage(lang);
        } catch (setLangErr) {
          // Non-blocking: UserContext reconcile effect will retry.
          console.warn('Login: setLanguage failed', setLangErr);
        }
      }
      // Log user login activity
      logUserActivity(email);
      // Record conversion if user came from public BiQ
      if (conversionRef) {
        await recordLoginConversion(conversionRef);
      }
      // Redirect to Bloom Terminal
      navigate(ROUTES.BLOOMTERMINAL);
    } catch (error) {
      console.error('Error during login:', error.code, error.message);

      // Handle Firebase auth errors
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError(t('errors.invalidCredentials'));
      } else if (error.code === 'auth/invalid-email') {
        setError(t('errors.invalidEmail'));
      } else if (error.code === 'auth/too-many-requests') {
        setError(t('errors.tooManyRequests'));
      } else if (error.code === 'auth/user-disabled') {
        setError(t('errors.accountDisabled'));
      } else {
        setError(t('errors.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title={`${t('auth.login')} - Bloom IQ`} />
      <AuthLayout variant="light">
        <div className="w-full">
          <AuthHeader
            title={t('auth.loginTitle')}
            subtitle={t('auth.loginSubtitle')}
          />

          {/* Launch Announcement - only show in development (private beta) */}
          {import.meta.env.DEV && (
            <div className="bg-[#E7F6EE] border border-[#DCE7DF] text-[#15803D] p-4 rounded-md mb-5 text-sm">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium">{t('auth.launchAnnouncement')}</p>
                  <p className="text-xs mt-1 text-[#16A34A]">{t('auth.privateBeta')}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <TextField
              label={t('auth.email')}
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              variant="light"
            />

            <PasswordField
              label={t('auth.password')}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              variant="light"
            />

            <div className="flex items-center justify-end">
              <Link
                to={localePath(lang, '/forgot-password')}
                className="text-[14px] text-[#5d625f] hover:text-bloom-green transition-colors"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <PrimaryButton
              type="submit"
              disabled={loading}
              loading={loading}
            >
              {t('auth.signIn')}
            </PrimaryButton>
          </form>

          <div className="mt-8 text-center text-[#5D625F] text-sm font-['Inter']">
            {t('auth.dontHaveAccount')}{' '}
            <Link
              to={localePath(lang, '/signup')}
              className={AUTH_LINK_CLASS}
            >
              {t('auth.signUp')}
            </Link>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[#C3C4C3]"></div>
            <span className="text-[#5D625F] text-[16px] font-['Inter']">{t('common.or')}</span>
            <div className="flex-1 h-px bg-[#C3C4C3]"></div>
          </div>

          {/* Social Login Buttons */}
          <SocialButtons
            onError={setError}
            isEmailAllowed={isEmailAllowed}
            successRoute={ROUTES.BLOOMTERMINAL}
            conversionRef={conversionRef}
            variant="light"
          />
        </div>
      </AuthLayout>
    </>
  );
};

export default Login;
